'use client';

import { useCallback, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';

import { unmask } from '@packages/gql/src/betterMasking';
import type { SearchProductCardItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardItemFragmentFragmentDoc';
import type { SearchProductCardResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardResultFragmentFragmentDoc';

import { ProductCardGridItem } from '@packages/modules/src/ProductCardV2/ProductCardV2.GridItem.client';

import { useProductImpressionTracking } from './tracking/useProductImpressionTracking';
import { showSeenProductsAtom } from '../../atoms/numberOfSeenProducts';
import { useDeferredRendering } from './useDeferredRendering';
import {
  SearchProductCardContent,
  type SearchProductCardContentProps,
} from './SearchProductCard.Content.client';
import { SearchProductCardPlaceholderContent } from './SearchProductCard.PlaceholderContent';
import { useOnFirstBecameVisible } from '../../hooks/useOnFirstBecameVisible';

export type SearchProductCardProps = Omit<SearchProductCardContentProps, 'hasUserInteraction'> & {
  alwaysRender?: boolean;
  onFirstBecameVisible?: (parameters: { pageNumber: number; indexInPage?: number }) => void;
  'aria-setsize'?: number;
  'aria-posinset'?: number;
  indexInPage: number;
};

/**
 * Specialized version of `ProductCard` for the search product grids
 */
export const SearchProductCard = ({
  alwaysRender,
  onFirstBecameVisible,
  indexInPage,
  ...contentProps
}: SearchProductCardProps) => {
  const {
    maskedItem,
    maskedResult,
    'aria-setsize': ariaSetsize,
    'aria-posinset': ariaPosinset,
    pageNumber,
  } = contentProps;

  const [hasUserInteraction, setHasUserInteraction] = useState(false);

  const item = unmask<typeof SearchProductCardItemFragmentFragmentDoc>(maskedItem);
  const result = unmask<typeof SearchProductCardResultFragmentFragmentDoc>(maskedResult);

  const { dispatchProductImpressionTracking } = useProductImpressionTracking(result, item);

  const ref = useRef<HTMLLIElement>(null);
  const showSeenProducts = useAtomValue(showSeenProductsAtom);

  useOnFirstBecameVisible(ref, () => {
    dispatchProductImpressionTracking();
    onFirstBecameVisible?.({ pageNumber, indexInPage });

    if (showSeenProducts) {
      ref.current?.classList.add('seen');
    }
  });

  const handleUserInteraction = useCallback(() => {
    setHasUserInteraction(true);
  }, []);

  // TODO SEARCH-3514 check again if we can just skip deferred rendering altogether, once the cards are even more optimized (wishlist button, color button tooltips) to get rendering time down from the current ~50ms to maybe 25ms or less
  const { showFullContents } = useDeferredRendering({ ref, alwaysRender });

  const { styleId, isHighlight } = item;

  return (
    <ProductCardGridItem
      className={`group pb-2 ${isHighlight ? 'highlight' : ''}`}
      ref={ref}
      id={styleId}
      onFocus={handleUserInteraction}
      onMouseOver={handleUserInteraction}
      aria-setsize={ariaSetsize}
      aria-posinset={ariaPosinset}
    >
      {showFullContents ? (
        <SearchProductCardContent {...contentProps} hasUserInteraction={hasUserInteraction} />
      ) : (
        <SearchProductCardPlaceholderContent
          maskedResult={maskedResult}
          maskedItem={maskedItem}
          pageNumber={pageNumber}
        />
      )}
    </ProductCardGridItem>
  );
};
